import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { CommonErrors, Grid, Tree } from "@/comn/components";
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useForm,
    useFetch,
    useModal,
    useStore,
    useToast,
    usePopup,
    useGrid,
    usePage,
    useTree,
    useResource,
    useAuth,
} from "@/comn/hooks"; // hook
import {
    BASE,
    URLS,
    APIS,
    SG_HS_MGMT_LST,
    SF_HS_MGMT_SRCH,
    SF_HS_DTL_SRCH,
    SG_HS_EXCEL_UPLOAD_LST,
} from "./services/OsciHsMgmtService"; // 서비스

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * HS Management List
 * !== HS 관리 목록 ==!
 */
export const OSCI0105002Q = (props: any) => {
    /*
     * @ 변수에 대한 주석 (기본 Hook 제외)
     * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
     * @ 기본 한줄 주석 형태를 사용
     * @ 포맷
     * @ [지환부호시작] [한글변수명] [지환부호종료]
     *
     * 예시) const pgeUid = "CGME0411001Q"; // Page Unique identifier !== 화면 고유 식별자 ==!
     * 선언과 동시에 전개가 이루어 지는 경우 변수의 위쪽에 작성
     */

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */

    const { pgeUid, menuId } = usePage({
        // auth: ["auth_clri0023", "auth_clri0022"] /
        auth: ["auth_osci0003"],
    }); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { openPopup } = usePopup();
    const { close, postMessage, getParams } = usePopup();
    let [query, setQuery] = useSearchParams();
    const [isSearch, setIsSearch] = useState(false);
    const [osciHsTreeLst, setOsciHsTreeLst] = useState([]);
    const [showColumn, setShowColumn] = useState(Number);
    const [mode, setMode] = useState("view");
    const tree = useTree();
    const { id } = useParams();
    const auth = useAuth();
    const authList = auth.get("authList") || [];
    const [authTp, setAuthTp] = useState(false);

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0031" },
            { area: "comnCd", comnCd: "OSC0048" },
            { area: "comnCd", comnCd: "OSC0047" },
        ],
    });
    /*
     * @ 화면에서 사용하는 form 객체를 선언
     * @ 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * @ 검색 조건일 경우 구분을 위해 폼 이름 뒤에 Srch
     * @ const form = {
     *      [form 이름] : useForm({
     *          defaultSchema: [form의 schema 구조],
     *          defaultValues: [form의 기본 값] || {},
     *      }),
     *      ...
     * }
     *
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // HS Management Search !== HS 관리 검색 ==!
        osciHsMgmtSrch: useForm({
            defaultSchema: SF_HS_MGMT_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
        // HS Detail Search !== HS 상세 검색 ==!
        hsDtlSrch: useForm({
            defaultSchema: SF_HS_DTL_SRCH,
            defaultValues: {},
        }),
    };

    /*
     * @ 화면에서 사용하는 grid 객체를 선언
     * @ 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * @ 그리드 변수명 뒤에 List (List) 를 붙여 구분
     * @ const grid = {
     *      [grid 이름] : useWijmo({
     *          defaultSchema: [grid의 schema 구조],
     *          page: [페이지 번호, 기본 0부터 시작],
                size: [한 페이지에 보여줄 데이터 갯수],
     *      }),
     *      ...
     * }
     * 
     * Declaration Grid
     * !== 그리드 정의 ==! 
     */
    const grid = {
        // HS Management List !== HS 관리 목록 ==!
        hsMgmtList: useGrid({
            defaultSchema: SG_HS_MGMT_LST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        //HS Upload List !== HS Upload 목록==!
        hsUploadLst: useGrid({
            defaultSchema: SG_HS_EXCEL_UPLOAD_LST,
        }),
    };

    /*
     * 화면에서 사용하는 api 호출 객체를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * Back-End Controller 메소드명과 동일하게 작성(Back-End 개발 가이드 참조)
     * 
     * 다건 조회    get         getRpckItmAppList
     * 
     * const fetch = {
     *      [fetch 이름] : useFetch({
     *          api: [fetch 실행 api, service 파일의 APIS 에 기재],
     *          enabled: [[fetch 가 실행되기 위한 조건]],
                key: [[fetch 에서 변화를 감지하여 자동실행할 변수]],
                onSuccess : [fetch 실행 성공시 실행],
                onError : [fetch 실행 실패시 실행],
                showToast: [성공/실패 결과 Toast메세지 표시 여부],
     *      }),
     *      ...
     * }
     * 
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */

    const fetch = {
        testSmplLst: useFetch({
            api: (page = grid.hsMgmtList.page) => {
                return APIS.getOsciHsMgmtPageList(form.osciHsMgmtSrch.getValues(), page, grid.hsMgmtList.size);
            },
            key: [grid.hsMgmtList.page, grid.hsMgmtList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.osciHsMgmtSrch.getValues(),
                    page: grid.hsMgmtList.page,
                    size: grid.hsMgmtList.size,
                });
            },
            onError: (data) => {
                console.log(data);
            },
            showToast: true,
        }),
        //Save the HS Code !== HS Code 저장==!
        saveOsciHsMgmt: useFetch({
            api: (data) => APIS.saveOsciHsMgmt({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        fetch.getOsciHsCdNode.fetch();
                        form.hsDtlSrch.setEditable(false);
                        setMode("view");
                    },
                    onCancel: () => {
                        return;
                    },
                });
            },
            onError: (error) => {
                if (error.response.data.errorCode === "-999") {
                    modal.openModal({
                        content: error.response.data.errorMessage,
                    });
                    form.hsDtlSrch.setErrors(error.response.data.errorMessage);
                } else {
                    toast.showToast({ type: "warning", content: "msg.osc.00015" });
                }
            },
        }),
        //Get the HS Code Node !== HS Code Node 조회==!
        getOsciHsCdNode: useFetch({
            api: (hsCd, dpth) => {
                return APIS.getOsciHsCdNode(hsCd, dpth);
            },
            enabled: !comnUtils.isPopup(),
            onSuccess: (data) => {
                const treeNode = data.osciHsMgmtDto.content.map((tree: any) => {
                    return {
                        id: tree.hsCd,
                        name: tree.hsCd + "." + tree.hsDesc,
                        hasChildren: true,
                        desc: tree.hsDesc,
                        useYn: tree.useYn,
                        regstId: tree.regstId,
                    };
                });
                tree.setData(treeNode);
            },
            onError: (error) => {
                console.log(error);
            },
            showToast: true,
        }),
        getOsciClcuTaxTest: useFetch({
            api: () => {
                return APIS.getOsciClcuTaxTest();
            },
            onSuccess: (data) => {
                console.log(data);
            },
            onError: (error) => {
                console.log(error);
            },
            showToast: true,
        }),
        //Save the HS EXCEL Upload !== HS Code 엑셀 업로드 저장==!
        saveOsciHsExcelUpload: useFetch({
            api: (data) => APIS.saveOsciHsExcelUpload(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        modal.closeModal();
                        fetch.getOsciHsCdNode.fetch();
                    },
                    onCancel: () => {
                        modal.closeModal();
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                if (error.response.status === 400) {
                    console.log(error.response.data);
                    form.hsDtlSrch.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: "msg.osc.00015",
                });
            },
        }),
    };

    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : HS 관리 그리드 클릭
     *      ...
     * }
     *
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        //Save the HS Code !== HS Code 저장==!
        saveOsciHsMgmt: () => {
            form.hsDtlSrch.handleSubmit(
                () => {
                    const data = { ...form.hsDtlSrch.getValues() };
                    if (showColumn == 2 || showColumn == 4 || showColumn == 6 || showColumn == 8 || showColumn == 12)
                        modal.openModal({
                            content: "msg.osc.00090",
                            onConfirm: () => {
                                fetch.saveOsciHsMgmt.fetch(data);
                            },
                        });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
        setHsFormData: (data: any) => {
            form.hsDtlSrch.setValue("hsCd", data.id);
            form.hsDtlSrch.setValue("hs" + data.id.length + "Desc", data.desc);
            form.hsDtlSrch.setValue("hsDesc", data.desc);
            form.hsDtlSrch.setValue("itmQtyUtCd", data.itmQtyUtCd);
            form.hsDtlSrch.setValue("hsDtyTpCd", data.hsDtyTpCd);
            form.hsDtlSrch.setValue("useYn", data.useYn);
            form.hsDtlSrch.setValue("regstId", data.regstId);
            setShowColumn(data.id.length);
        },
        getOsciClcuTaxTest: () => {
            fetch.getOsciClcuTaxTest.fetch();
        },
        //Save the HS EXCEL Upload !== HS Code 엑셀 업로드 저장==!
        saveOsciHsExcelUpload: () => {
            form.hsDtlSrch.handleSubmit(
                () => {
                    modal.openModal({
                        content: "msg.osc.00090",
                        onConfirm: () => {
                            let gridData = grid.hsUploadLst.getData();
                            let isDupc = false;
                            let dupcRow: number = 0;

                            gridData.map((item: any, index) => {
                                if (gridData.filter((i: any) => i.hsCd == item.hsCd).length > 1) {
                                    isDupc = true;
                                    dupcRow = index + 1;
                                }
                            });
                            if (isDupc) {
                                modal.openModal({
                                    content: t("L_ROW_NO") + ` [${dupcRow}] : ` + t("msg.osc.00012"), // Duplicate data exists..
                                });

                                return;
                            }
                            const errors = grid.hsUploadLst.validate();
                            console.log(errors);
                            // 오류가 존재하는 경우
                            if (errors) {
                                // 공통 오류팝업창에 해당 오류 내용을 표시
                                modal.openModal({
                                    content: <CommonErrors {...errors} />,
                                    draggable: true,
                                    size: "lg",
                                });

                                return;
                            }

                            const osciHsCdDto = {
                                osciHsCdDtoList: grid.hsUploadLst.getData(),
                            };
                            fetch.saveOsciHsExcelUpload.fetch(osciHsCdDto);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
    };

    const render = {
        click_Grid_HsDtlSelc: {
            head: {},
            cell: {
                hsCd: (props: any) => {
                    const { binding, rowValues, value } = props;

                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                console.log(rowValues);
                                // modal.postMessage({ code: rowValues.epcCd, label: rowValues.epcDesc });
                                //close();
                            }}
                        >
                            {props.value}
                        </a>
                    );
                },
            },
            edit: {},
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        setAuthTp(true);

        // handler.getOsciHsMgmtPageList();
        // handler.getOsciHsMgmtTreeList();
        form.hsDtlSrch.setEditable(false);
    }, []);

    /*
     * @ 라벨 생성(명명 규칙)
     * @ prefix
     * @ 일반라벨 : L_
     * @ 버튼 : B_
     * @ 제목 : T_
     * @ 메세지 : msg.00000 [msg.숫자5자리] , 00000 ~ 10000 까지는 공통영역
     * @ 매세지를 제외한 라벨은 메타 단어, 용어 조합, 메세지는 숫자 5자리 일련번호로 정의
     * @ 대문자로 표시
     * @ 단어와 단어 사이 용어와 용어 사이는 "_" 로 구분
     */

    return (
        <Page>
            <>
                <Layout direction="row">
                    <Layout.Left>
                        <Button
                            role="excelUpload"
                            hidden={!authTp}
                            onClick={() => {
                                grid.hsUploadLst.clearData();
                                modal.openModal({
                                    size: "lg",
                                    content: (
                                        <>
                                            <Group.Title title="Excel Upload Hs Code List" titleSize={1} />
                                            <Group>
                                                <Group.Body>
                                                    <Layout>
                                                        <Layout.Left>
                                                            <Button
                                                                role="tmplDown"
                                                                onClick={() => {
                                                                    comnUtils.downloadTemplate(
                                                                        "8c128df8-d-79-c-4e-e2-a-acb-9fa4d45b6a1b",
                                                                    );
                                                                }}
                                                            ></Button>
                                                            <Button
                                                                role="excelUpload"
                                                                onClick={async () => {
                                                                    // 업로드할 엑셀 파일을 선택하여 엑셀 파일 Parsing 후 데이터를 return
                                                                    // await 를 사용하여 엑셀 데이터를 처리 완료 할때까지 기다린 후 실행
                                                                    // headers : 엑셀의 Header Row의 갯수를 지정하여 데이터 Parsing 시 데이터에 들어가지 않도록 처리( 기본값 1 )
                                                                    // bindings : 그리드 스키마에 지정된 셀 이외의 다른 셀도 업로드 데이터에 추가 할 경우 Cell Name : binding 으로 값을 추가하여 처리 가능
                                                                    const data = await grid.hsUploadLst.importExcel({
                                                                        headers: 1,
                                                                        bindings: {},
                                                                        sheet: 0,
                                                                    });
                                                                    grid.hsUploadLst.clearData();

                                                                    data.forEach((item: any) => {
                                                                        grid.hsUploadLst.addRow(item);
                                                                    });
                                                                }}
                                                            ></Button>
                                                        </Layout.Left>
                                                    </Layout>
                                                    <Grid {...grid.hsUploadLst.grid} />
                                                    <Layout>
                                                        <Layout.Right>
                                                            <Button
                                                                role="save"
                                                                onClick={() => {
                                                                    handler.saveOsciHsExcelUpload();
                                                                }}
                                                            ></Button>
                                                        </Layout.Right>
                                                    </Layout>
                                                </Group.Body>
                                            </Group>
                                        </>
                                    ),
                                });
                            }}
                        ></Button>
                        <Button
                            role="excelDown"
                            onClick={() => {
                                comnUtils.exportExcel({
                                    // 엑셀 다운로드 주소
                                    url: BASE.api + "/excel",
                                    // back-end에 전송할 데이터
                                    data: {},
                                    // 다운로드할 엑셀 파일명(확장자 없이 기재)
                                    fileName: "HS CODE LIST",
                                });
                            }}
                        ></Button>
                    </Layout.Left>
                </Layout>
                <Layout gap={4}>
                    <Layout>
                        <Group>
                            <Group.Body>
                                <Group.Section>
                                    <Tree
                                        {...tree.tree}
                                        checkbox={false}
                                        height={500}
                                        onOpen={async (e) => {
                                            const hsSubNode = await APIS.getOsciHsCdNode(e.id, e.depth);
                                            const treeSubNode = hsSubNode.data.osciHsMgmtDto.content.map(
                                                (data: any) => {
                                                    return {
                                                        id: data.hsCd,
                                                        name: data.hsCd + "." + data.hsDesc,
                                                        hasChildren: e.depth == 3 ? false : true,
                                                        desc: data.hsDesc,
                                                        itmQtyUtCd: data.itmQtyUtCd,
                                                        hsDtyTpCd: data.hsDtyTpCd,
                                                        regstId: data.regstId,
                                                        useYn: data.useYn,
                                                    };
                                                },
                                            );
                                            tree.setChildren(e, treeSubNode);
                                            handler.setHsFormData(e);
                                        }}
                                        onClick={async (e) => {
                                            if (e.depth < 4) tree.openItem(e);

                                            handler.setHsFormData(e);
                                        }}
                                    />
                                </Group.Section>
                            </Group.Body>
                        </Group>
                    </Layout>
                    <Layout>
                        <Group>
                            <Group.Body>
                                <Group.Section>
                                    <Group.Row>
                                        {/* hsCd !== hsCd ==!  */}
                                        <Group.Control
                                            {...form.hsDtlSrch.schema.hsCd}
                                            onChange={(e) => {
                                                setShowColumn(e.replaceAll(".", "").length);
                                            }}
                                        />
                                    </Group.Row>
                                    <Group.Row>
                                        {/* hs2Cd !== hs2Cd ==!  */}
                                        <Group.Control
                                            {...form.hsDtlSrch.schema.hs2Desc}
                                            readOnly={showColumn == 2 ? false : true}
                                        />
                                    </Group.Row>
                                    {showColumn >= 4 && (
                                        <Group.Row>
                                            {/* hs4Cd !== hs4Cd ==!  */}
                                            <Group.Control
                                                {...form.hsDtlSrch.schema.hs4Desc}
                                                readOnly={showColumn == 4 ? false : true}
                                            />
                                        </Group.Row>
                                    )}
                                    {showColumn >= 6 && (
                                        <Group.Row>
                                            {/* hs6Cd !== hs6Cd ==!  */}
                                            <Group.Control
                                                {...form.hsDtlSrch.schema.hs6Desc}
                                                readOnly={showColumn == 6 ? false : true}
                                            />
                                        </Group.Row>
                                    )}
                                    {showColumn >= 8 && (
                                        <Group.Row>
                                            {/* hs6Cd !== hs8Cd ==!  */}
                                            <Group.Control
                                                {...form.hsDtlSrch.schema.hs8Desc}
                                                readOnly={showColumn == 8 ? false : true}
                                            />
                                        </Group.Row>
                                    )}
                                    {showColumn >= 12 && (
                                        <Group.Row>
                                            {/* hsCd !== hsDesc ==!  */}
                                            <Group.Control
                                                {...form.hsDtlSrch.schema.hsDesc}
                                                readOnly={showColumn == 12 ? false : true}
                                            />
                                        </Group.Row>
                                    )}
                                    <Group.Row>
                                        {/* hsCd !== qty ==!  */}
                                        {showColumn >= 12 && <Group.Control {...form.hsDtlSrch.schema.itmQtyUtCd} />}
                                        {/* hsCd !== useYn ==!  */}
                                        <Group.Control {...form.hsDtlSrch.schema.useYn} />
                                    </Group.Row>
                                    {showColumn >= 12 && (
                                        <Group.Row>
                                            <Group.Control {...form.hsDtlSrch.schema.brndYn} />
                                            <Group.Control {...form.hsDtlSrch.schema.cpntYn} />
                                        </Group.Row>
                                    )}
                                    <Group.Row>
                                        {/* hsCd !== register ==!  */}
                                        <Group.Control {...form.hsDtlSrch.schema.regstId} />
                                    </Group.Row>
                                </Group.Section>
                            </Group.Body>
                        </Group>
                    </Layout>
                </Layout>
                <Layout>
                    <Layout.Right>
                        {mode == "view" && authTp && (
                            <Button
                                role="edit"
                                onClick={() => {
                                    form.hsDtlSrch.setEditable(true);
                                    form.hsDtlSrch.setEditable("regstId", false);
                                    form.hsDtlSrch.setValue("regstId", auth.get("usrId"));
                                    setMode("updt");
                                }}
                            ></Button>
                        )}
                        {mode == "updt" && authTp && (
                            <>
                                <Button
                                    role="save"
                                    onClick={() => {
                                        handler.saveOsciHsMgmt();
                                    }}
                                ></Button>
                                <Button
                                    role="cancel"
                                    onClick={() => {
                                        form.hsDtlSrch.setEditable(false);
                                        setMode("view");
                                    }}
                                ></Button>
                            </>
                        )}
                    </Layout.Right>
                </Layout>
            </>
        </Page>
    );
};
